import { ListItem } from '@tableau/widgets';
import {
  TemplateThumbnailImage,
  WorkbookTemplateDescription
} from '@tableau/workbook-templates-widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { Location } from '../../../common/react/Location';
import { StartersDialogClickSourceOptions, StarterWorkbookCreationSourceOptions } from '../../../common/telemetryEvents/TelemetryEvents';
import { StartersDropdownConnectionTypeChangeddTelemetryEvent, StarterWorkbookCreationClickedTelemtryEvent } from '../../../common/telemetryEvents/util/StartersTelemetry';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { displayDialogWithTemplate } from '../../../starters/components/InteractiveStarterModal';
import { StartersSpaceStyle } from '../../../starters/styles/StarterStyles';
import { Starter, StartersGroupedByConnectionType } from '../../../starters/types/StartersTypes';
import { buildSessionRelevantInfo } from '../../../starters/utils/startersHelper';
import { SpaceContainerStyles } from '../../../style/SpaceContainerStyles';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { StartersSpaceDropDown } from './StartersSpaceDropDown';

const NUMBER_OF_STARTERS_PER_CONNECTION_TYPE = 8;
export interface StartersSpaceOwnProps {
  readonly selectedConnectionTypeInternalName?: string;
  readonly urls: SpacesSiteUrls;
  readonly sessionManagementService: SessionManagementService;
  readonly toasterService: ToasterService;
}

export interface StartersSpaceStateProps {
  readonly startersGroupedByConnectionType?: StartersGroupedByConnectionType;
  readonly isBusy: boolean;
}

export interface StartersSpaceState {
  readonly selectedConnectionTypeInternalName: string;
  readonly dropDownChangeEvent: Boolean;
}

export interface StartersSpaceDispatchProps {
  fetchStartersData: () => void;
}

export interface DropDownListType {
  items: ListItem[];
  selectedValue: string;
}

export type StartersSpaceNonAngularProps = StartersSpaceOwnProps & StartersSpaceStateProps & StartersSpaceDispatchProps;
export type StartersSpaceProps = StartersSpaceNonAngularProps;

export class StartersSpace extends React.PureComponent<StartersSpaceProps, StartersSpaceState> {
  private readonly allOption: ListItem;

  public constructor(props: StartersSpaceProps) {
    super(props);
    this.allOption = {value: 'all', content: 'All'};
    this.state = {
      selectedConnectionTypeInternalName: this.isValidConnectionType(this.props.selectedConnectionTypeInternalName) ? this.props.selectedConnectionTypeInternalName : this.allOption.value,
      dropDownChangeEvent: false,
    };
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.starters() });
  }

  public componentDidMount() {
    this.props.fetchStartersData();
  }

  public componentDidUpdate(prevProps: StartersSpaceProps) {
    // There are two options to get to componentDidUpdate function:
    // 1. If the user has selected a connection type throught the dropdown
    // 2. If the user manually changed the url manually (which should be supported), in this case the parent component will update selectedConnectionTypeInternalName
    // that derives from the url.

    if (this.state.dropDownChangeEvent) {
      this.setState({ dropDownChangeEvent: false });
    } else if ((this.state.selectedConnectionTypeInternalName !== this.props.selectedConnectionTypeInternalName) && (prevProps !== this.props)){
      if (this.isValidConnectionType(this.props.selectedConnectionTypeInternalName)) {
        this.updateStateConnectionTypeAndUrl(this.props.selectedConnectionTypeInternalName, false);
      } else {
        this.updateStateConnectionTypeAndUrl(this.allOption.value, false);
      }
    }
  }

  private isValidConnectionType(connectionType: string) {
    return (connectionType in this.props.startersGroupedByConnectionType);
  }

  private updateStateConnectionTypeAndUrl(selectedConnectionTypeInternalName: string, dropDownChangeEvent: boolean) {
    this.setState({
      selectedConnectionTypeInternalName,
      dropDownChangeEvent,
    });
    Location.instance.setUrl(this.props.urls.startersByConnectionTypeUrl(selectedConnectionTypeInternalName));
  }

  private onConnectionTypeSelected = (selectedConnectionTypeInternalName: string) => {
    this.updateStateConnectionTypeAndUrl(selectedConnectionTypeInternalName, true);
    StartersDropdownConnectionTypeChangeddTelemetryEvent(selectedConnectionTypeInternalName);
  }

  private getConnectionTypesDropDownList = (): ListItem[] => {
    let displayNames: ListItem[] = [this.allOption];
    Object.entries(this.props.startersGroupedByConnectionType).map(([connectionType, startersList]) => {
      displayNames.push({value: connectionType, content: startersList[0].connectionType.displayName});
    });
    return displayNames;
  }

  private buildDropDownItems = (): DropDownListType => {
    return {
      items: this.getConnectionTypesDropDownList(),
      selectedValue: this.state.selectedConnectionTypeInternalName,
    };
  }

  private onTemplateFailed = (): void => {
    this.props.toasterService.pop({
      text: Messages.startersWorkbookCreationError(),
      type: ToastType.Error,
    });
  }

  private sendWorkbookCreationTelemetry = (starter: Starter, withSampleData: boolean) => {
    StarterWorkbookCreationClickedTelemtryEvent(
      StarterWorkbookCreationSourceOptions.Space,
      starter,
      withSampleData,
    );
  }

  public render(): JSX.Element {
    return(
      <div className={SpaceContainerStyles.SpaceContainer}>
        <div className={SpaceContainerStyles.MastheadArea}>
          <div className={ExplorerStyles.SpaceHeaderContent}>
            <div className={ExplorerStyles.SpaceHeaderText}>{Messages.starters()}</div>
          </div>
          <div className={StartersSpaceStyle.SpaceSubHeader}>{Messages.startersSubHeader()}</div>
        </div>
        {this.getSubPlaceArea()}
      </div>
    );
  }

  private getSubPlaceArea(): JSX.Element {
    if (!this.props.isBusy) {
      if (Object.entries(this.props.startersGroupedByConnectionType).length > 0) {
        return (
          <div className={SpaceContainerStyles.SubplaceArea}>
            <div className= {StartersSpaceStyle.StartersToolBar}>
              <StartersSpaceDropDown
                allOption={this.allOption}
                dropDownItems={this.buildDropDownItems()}
                onConnectionTypeSelected={this.onConnectionTypeSelected}
              />
            </div>
            {this.getStarters()}
          </div>
        );
      }
      else {
        return (
          <div className={StartersSpaceStyle.startersEmptySpace}>{Messages.startersEmptyMessage()}</div>
        );
      }
    }
  }

  private getStarters(): JSX.Element {
    let startersList: Starter[] = [];

    if (this.state.selectedConnectionTypeInternalName === this.allOption.value) {
      for (let connectionTypeStartersList of Object.values(this.props.startersGroupedByConnectionType)) {
        startersList.push(...connectionTypeStartersList.slice(0, NUMBER_OF_STARTERS_PER_CONNECTION_TYPE));
      }
    } else if (this.state.selectedConnectionTypeInternalName in this.props.startersGroupedByConnectionType) {
      startersList = this.props.startersGroupedByConnectionType[this.state.selectedConnectionTypeInternalName].slice(0, NUMBER_OF_STARTERS_PER_CONNECTION_TYPE);
    }

    const starters = startersList.map((starter) => {
      return (
        <React.Fragment key={`starter-${starter.id}`}>
          <div className={StartersSpaceStyle.startersSpaceStartersAreaItem}>
            <div className={StartersSpaceStyle.startersSpaceStartersAreaItemImage}>
              <TemplateThumbnailImage
                onClick={ () => displayDialogWithTemplate(
                  starter,
                  this.props.startersGroupedByConnectionType[starter.connectionType.internalName],
                  StartersDialogClickSourceOptions.Space,
                  this.props.startersGroupedByConnectionType[starter.connectionType.internalName].findIndex((starterInternal) => starterInternal === starter)
                )}
                imageHeight={StartersSpaceStyle.thumbnailImageSize.height}
                imageWidth={StartersSpaceStyle.thumbnailImageSize.width}
                onFinishedLoading={_.noop}
                template={starter}
              />
            </div>
            <div className={StartersSpaceStyle.startersSpaceStartersAreaItemDescription}>
              <WorkbookTemplateDescription
                template={starter}
                onTemplateSelected={_.noop}
                onFinishedLoading={_.noop}
                isVpc={true}
                helpVersion={HelpService.externalVersion()}
                onTemplateCreationFailed={this.onTemplateFailed}
                sendTelemetry={this.sendWorkbookCreationTelemetry}
                sessionRelevantInfo={buildSessionRelevantInfo(this.props.sessionManagementService)}
              />
            </div>
          </div>
        </React.Fragment>
      );
    });

    return <div className={StartersSpaceStyle.startersSpaceStartersArea}> {starters} </div>;
  }
}
